/**
 * Copilot — Cmd+K command bar with conversational AI.
 *
 * Features:
 * - Cmd/Ctrl+K to open
 * - Natural language commands in French
 * - Live preview before execution (for invoice creation)
 * - Quota indicator
 * - Markdown-light rendering
 *
 * Backend: /api/copilot/chat → /api/copilot/execute
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Sparkles, Send, X, Loader2, Check, AlertCircle, Zap, ChevronRight, Mic, MicOff } from 'lucide-react';
import { copilotAPI } from '../services/copilotAPI';
import { api } from '../services/api';

const SUGGESTIONS = [
  'Combien j\'ai facturé ce mois-ci ?',
  'Liste mes factures impayées',
  'Quel est mon meilleur client ?',
  'Facture Dupont SARL, 3 jours de conseil à 800€/jour',
  'Relance les factures en retard de plus de 30 jours',
];

function formatMarkdownLight(text) {
  if (!text) return null;
  // Lightweight rendering: **bold** and bullet lists.
  return text.split('\n').map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={i} className="h-2" />;
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    const rendered = parts.map((p, j) =>
      p.startsWith('**') && p.endsWith('**') ? (
        <strong key={j} className="font-semibold text-slate-900">
          {p.slice(2, -2)}
        </strong>
      ) : (
        <span key={j}>{p}</span>
      )
    );
    if (trimmed.startsWith('- ')) {
      return (
        <div key={i} className="flex gap-2 ml-1 leading-relaxed">
          <span className="text-emerald-500">•</span>
          <span>{rendered.map((r, k) => r.props?.children?.startsWith?.('- ') ? r.props.children.slice(2) : r)}</span>
        </div>
      );
    }
    return <div key={i} className="leading-relaxed">{rendered}</div>;
  });
}

export default function Copilot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]); // {role: 'user'|'assistant'|'preview', content, intent, args, executed}
  const [loading, setLoading] = useState(false);
  const [quota, setQuota] = useState(null);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const sessionIdRef = useRef(`copilot_${Date.now()}`);

  // Voice recording (Whisper)
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const stopRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      mr.stop();
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (recording || transcribing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        if (chunksRef.current.length === 0) return;
        setTranscribing(true);
        try {
          const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
          const form = new FormData();
          form.append('audio', blob, 'voice.webm');
          const { data } = await api.post('/copilot/transcribe', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const transcript = (data.text || '').trim();
          if (transcript) {
            setInput(transcript);
            // Auto-submit after short delay so user can see the transcript
            setTimeout(() => submit(transcript), 300);
          }
        } catch (e) {
          /* swallow — user can retype */
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      /* mic permission denied or not available */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording, transcribing]);

  // Toggle with Cmd+K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Fetch quota on open
  useEffect(() => {
    if (!open) return;
    copilotAPI
      .getUsage()
      .then((r) => setQuota(r.data))
      .catch(() => {});
  }, [open]);

  const submit = useCallback(
    async (rawText) => {
      const text = (rawText ?? input).trim();
      if (!text || loading) return;
      setInput('');
      setMessages((m) => [...m, { role: 'user', content: text }]);
      setLoading(true);

      try {
        const res = await copilotAPI.chat(text, sessionIdRef.current);
        const { intent, args, speech, confirmation_required, preview, quota: q } = res.data;
        setQuota(q);

        // If no confirmation needed, execute right away (read-only intents)
        if (!confirmation_required && intent !== 'unknown' && intent !== 'help') {
          try {
            const exec = await copilotAPI.execute(intent, args);
            setMessages((m) => [
              ...m,
              { role: 'assistant', content: exec.data.message || speech, executed: true },
            ]);
          } catch (e) {
            setMessages((m) => [
              ...m,
              { role: 'assistant', content: e?.response?.data?.detail?.message || 'Une erreur est survenue.', error: true },
            ]);
          }
        } else if (intent === 'unknown') {
          setMessages((m) => [
            ...m,
            { role: 'assistant', content: args?.response || speech || "Je n'ai pas compris. Pouvez-vous reformuler ?" },
          ]);
        } else if (intent === 'help') {
          setMessages((m) => [
            ...m,
            {
              role: 'assistant',
              content:
                "Je peux vous aider à :\n- Créer une facture en une phrase\n- Voir vos statistiques\n- Lister vos factures (impayées, du mois...)\n- Identifier vos meilleurs clients\n- Préparer des relances\n\nEssayez : *« Facture Dupont SARL 3 jours à 800€ »*",
            },
          ]);
        } else {
          // Confirmation flow: show preview
          setMessages((m) => [
            ...m,
            { role: 'preview', content: speech, intent, args, preview },
          ]);
        }
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const msg =
          (detail && typeof detail === 'object' && detail.message) ||
          (typeof detail === 'string' ? detail : 'Erreur de communication avec le Copilote.');
        setMessages((m) => [...m, { role: 'assistant', content: msg, error: true }]);
      } finally {
        setLoading(false);
      }
    },
    [input, loading]
  );

  const confirmExec = useCallback(
    async (msgIdx) => {
      const msg = messages[msgIdx];
      if (!msg || msg.role !== 'preview') return;
      setLoading(true);
      try {
        const exec = await copilotAPI.execute(msg.intent, msg.args);
        setMessages((m) =>
          m.map((mm, i) =>
            i === msgIdx
              ? { role: 'assistant', content: exec.data.message, executed: true, redirect: exec.data.data?.redirect }
              : mm
          )
        );
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const errMsg =
          (detail && typeof detail === 'object' && detail.message) ||
          'Une erreur est survenue lors de l\'exécution.';
        setMessages((m) =>
          m.map((mm, i) => (i === msgIdx ? { role: 'assistant', content: errMsg, error: true } : mm))
        );
      } finally {
        setLoading(false);
      }
    },
    [messages]
  );

  const cancelPreview = useCallback((msgIdx) => {
    setMessages((m) =>
      m.map((mm, i) => (i === msgIdx ? { role: 'assistant', content: 'Action annulée.' } : mm))
    );
  }, []);

  return (
    <>
      {/* Floating trigger button (mobile + desktop fallback) */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 px-4 py-3 rounded-full bg-emerald-500 text-white font-semibold shadow-xl shadow-emerald-500/40 hover:bg-emerald-600 hover:scale-105 transition-all"
        data-testid="copilot-trigger"
        aria-label="Ouvrir le Copilote IA"
      >
        <Sparkles className="h-4 w-4" />
        <span className="hidden sm:inline text-sm">Copilote IA</span>
        <kbd className="hidden md:inline text-[10px] bg-white/20 px-1.5 py-0.5 rounded font-mono">⌘K</kbd>
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[90] flex items-start justify-center pt-[8vh] px-4 bg-black/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="copilot-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-[80vh]"
            data-testid="copilot-panel"
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3 bg-gradient-to-br from-emerald-50 to-white">
              <div className="h-9 w-9 rounded-xl bg-emerald-500 flex items-center justify-center shadow-md shadow-emerald-500/30">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm">Copilote IA</div>
                <div className="text-xs text-slate-500">
                  {quota
                    ? quota.limit === -1
                      ? 'Illimité ✨'
                      : `${quota.remaining} commande(s) restante(s) ce mois`
                    : 'Tapez votre commande en français'}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 transition"
                data-testid="copilot-close"
                aria-label="Fermer"
              >
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {messages.length === 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-500 mb-3">💡 Quelques exemples pour commencer :</p>
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => submit(s)}
                      className="w-full text-left px-4 py-3 rounded-xl bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-200 text-sm text-slate-700 transition flex items-center justify-between group"
                      data-testid="copilot-suggestion"
                    >
                      <span>{s}</span>
                      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-500 transition" />
                    </button>
                  ))}
                </div>
              )}

              {messages.map((m, i) => {
                if (m.role === 'user') {
                  return (
                    <div key={i} className="flex justify-end">
                      <div className="max-w-[85%] px-4 py-2.5 rounded-2xl rounded-br-md bg-emerald-500 text-white text-sm">
                        {m.content}
                      </div>
                    </div>
                  );
                }
                if (m.role === 'preview') {
                  const p = m.preview || {};
                  return (
                    <div key={i} className="flex justify-start">
                      <div className="max-w-[90%] w-full">
                        <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-slate-50 border border-slate-200 text-sm text-slate-700 mb-2">
                          {m.content}
                        </div>
                        <div className="bg-white border border-emerald-200 rounded-xl p-4 shadow-sm">
                          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2">
                            Aperçu
                          </div>
                          <div className="text-sm text-slate-900 font-semibold mb-2">
                            {p.client_name}
                          </div>
                          {(p.items || []).map((it, k) => (
                            <div key={k} className="text-sm text-slate-600 flex justify-between py-1">
                              <span>
                                {it.description} · {it.quantity} × {it.unit_price}€
                              </span>
                              <span className="font-medium text-slate-900">
                                {(it.quantity * it.unit_price).toFixed(2)}€
                              </span>
                            </div>
                          ))}
                          <div className="border-t border-slate-100 mt-2 pt-2 flex justify-between text-sm">
                            <span className="text-slate-500">Total HT</span>
                            <span className="font-semibold">{p.total_ht?.toFixed(2)}€</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">TVA</span>
                            <span>{p.total_tva?.toFixed(2)}€</span>
                          </div>
                          <div className="flex justify-between text-base mt-1.5">
                            <span className="font-semibold">Total TTC</span>
                            <span className="font-bold text-emerald-600">
                              {p.grand_total?.toFixed(2)}€
                            </span>
                          </div>
                          <div className="text-xs text-slate-400 mt-1">
                            Échéance dans {p.due_days} jours
                          </div>
                          <div className="flex gap-2 mt-4">
                            <button
                              onClick={() => confirmExec(i)}
                              disabled={loading}
                              className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold text-sm py-2.5 rounded-lg flex items-center justify-center gap-1.5 transition"
                              data-testid="copilot-confirm"
                            >
                              <Check className="h-4 w-4" />
                              Créer la facture
                            </button>
                            <button
                              onClick={() => cancelPreview(i)}
                              className="px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition"
                              data-testid="copilot-cancel"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={i} className="flex justify-start">
                    <div
                      className={`max-w-[90%] px-4 py-3 rounded-2xl rounded-bl-md text-sm ${
                        m.error
                          ? 'bg-red-50 text-red-700 border border-red-100'
                          : m.executed
                          ? 'bg-emerald-50 text-slate-800 border border-emerald-100'
                          : 'bg-slate-50 text-slate-700 border border-slate-200'
                      }`}
                    >
                      {m.error && <AlertCircle className="inline h-4 w-4 mr-1.5 mb-0.5" />}
                      <div className="space-y-1">{formatMarkdownLight(m.content)}</div>
                      {m.redirect && (
                        <a
                          href={m.redirect}
                          className="inline-flex items-center gap-1 text-emerald-600 font-semibold text-sm mt-2 hover:underline"
                          data-testid="copilot-result-link"
                        >
                          Voir la facture
                          <ChevronRight className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-50 px-4 py-2.5 rounded-2xl rounded-bl-md border border-slate-200">
                    <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-slate-100 p-3 bg-white">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  submit();
                }}
                className="flex items-center gap-2"
              >
                <div className="flex-1 relative">
                  <Zap className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
                  <input
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={recording ? 'Écoute en cours...' : transcribing ? 'Transcription...' : 'Tapez ou dictez votre commande...'}
                    className="w-full pl-9 pr-4 py-3 bg-slate-50 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:bg-white transition"
                    disabled={loading || recording || transcribing}
                    data-testid="copilot-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={recording ? stopRecording : startRecording}
                  disabled={loading || transcribing}
                  className={`h-11 w-11 flex items-center justify-center rounded-xl transition shadow-md ${
                    recording
                      ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse shadow-red-500/30'
                      : 'bg-slate-100 hover:bg-emerald-100 text-slate-600 hover:text-emerald-700'
                  } disabled:opacity-40`}
                  data-testid="copilot-mic"
                  aria-label={recording ? 'Arrêter l\'enregistrement' : 'Commande vocale'}
                >
                  {transcribing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : recording ? (
                    <MicOff className="h-4 w-4" />
                  ) : (
                    <Mic className="h-4 w-4" />
                  )}
                </button>
                <button
                  type="submit"
                  disabled={!input.trim() || loading || recording || transcribing}
                  className="h-11 w-11 flex items-center justify-center rounded-xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white transition shadow-md shadow-emerald-500/20"
                  data-testid="copilot-send"
                  aria-label="Envoyer"
                >
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
